import React from 'react';
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";

interface ModalActionsProps {
  onCancel: () => void;
  onSubmit: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
  cancelDisabled?: boolean;
  submitVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  className?: string;
  showFooter?: boolean;
}

/**
 * Reusable modal action buttons component
 * Standardizes submit/cancel button patterns across modals
 */
export function ModalActions({
  onCancel,
  onSubmit,
  isLoading = false,
  submitLabel = "Submit",
  cancelLabel = "Cancel",
  submitDisabled = false,
  cancelDisabled = false,
  submitVariant = "default",
  className = "",
  showFooter = true
}: ModalActionsProps) {
  
  const actionButtons = (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button 
        type="button"
        variant="outline" 
        onClick={onCancel} 
        disabled={cancelDisabled || isLoading}
        className="h-9"
      >
        {cancelLabel}
      </Button>
      <Button
        type="button"
        variant={submitVariant}
        onClick={onSubmit}
        disabled={submitDisabled || isLoading}
        className="h-9"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </div>
  );

  return showFooter ? (
    <DialogFooter className="pt-4">
      {actionButtons}
    </DialogFooter>
  ) : (
    actionButtons
  );
}

// Specialized variants for common use cases
export function SaveCancelActions(props: Omit<ModalActionsProps, 'submitLabel'>) {
  return <ModalActions {...props} submitLabel="Save" />;
}

export function CreateCancelActions(props: Omit<ModalActionsProps, 'submitLabel'>) {
  return <ModalActions {...props} submitLabel="Create" />;
}

export function SubmitCancelActions(props: Omit<ModalActionsProps, 'submitLabel'>) {
  return <ModalActions {...props} submitLabel="Submit" />;
}

export function UpdateCancelActions(props: Omit<ModalActionsProps, 'submitLabel'>) {
  return <ModalActions {...props} submitLabel="Update" />;
}
